//* Import React
import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Tab, Tabs } from "@material-ui/core";

//* Import components
import { Grid } from "../../../component/list/grid";
import DefaultButton from "../../../component/base/DefaultButton";
import { RiArrowGoBackLine } from "react-icons/ri";
import ModalManage from "../../QuestionsView/list/components/ModalManage";
import { ModalUpdateStatus } from "./components/ModalUpdateStatus";

//* Import custom hook
import { useStudentResponseObjectives } from "../../../hooks/studentResponseObjectives/useStudentResponseObjectives";

//* Import utils
import {
  mapDifficultyLevel,
  mapEducationLevel,
} from "../../../utils/questions/questionsMap";

//* Import types
import type { StudentResponseObjectives } from "../../../hooks/studentResponseObjectives/types/types";
import type { Pagination } from "../../../services/stores/types";

var debounce = require("lodash.debounce");

/**
 * @returns {JSX.Element} StudentResponsesView component.
 * @description This component is responsible for displaying the student responses.
 */
export function StudentResponsesObjectivesView() {
  const { answerSheetId, studentId } = useParams();
  const token = localStorage.getItem("token") || "";

  const {
    getStudentResponsesByStudent,
    updateStudentResponse,
  } = useStudentResponseObjectives(token);

  const [studentResponses, setStudentResponses] = useState<
    StudentResponseObjectives[]
  >([]);
  const [studentName, setStudentName] = useState<string>("");
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 20,
    totalItems: 0,
  });
  const [loadingResponses, setLoadingResponses] = useState<boolean>(true);

  const [searchInputValue, setSearchInputValue] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isSearching, setIsSearching] = useState<boolean>(false);

  const [modalQuestionId, setModalQuestionId] = useState<string | null>(null);
  const [showModalManage, setShowModalManage] = useState<boolean>(false);

  const [showModalUpdateStatus, setShowModalUpdateStatus] =
    useState<boolean>(false);
  const [selectedResponseId, setSelectedResponseId] = useState<string>("");
  const [selectedResponseStatus, setSelectedResponseStatus] = useState<
    "correct" | "wrong" | "notMarked"
  >("correct");

  const [activeTab, setActiveTab] = useState<number>(0);

  const tabs = useMemo(
    () => [
      { label: "Todos", status: null }, // index 0
      { label: "Corretas", status: "correct" }, // index 1
      { label: "Incorretas", status: "wrong" }, // index 2
      { label: "Não Marcadas", status: "not_marked" }, // index 3
      { label: "Erros", status: "error" }, // index 4
    ],
    []
  );

  /**
   * @param {string | null} status - The status to filter the responses by.
   * @param {number} page - The page number to fetch.
   * @param {string} searchTerm - The search term to filter the responses by.
   * @returns {Promise<void>}
   * @description This function fetches the student responses by status.
   */
  const fetchResponsesByStatus = useCallback(
    async (status: string | null, page: number = 1, searchTerm?: string) => {
      if (!answerSheetId || !studentId) return;
      try {
        setLoadingResponses(true);
        const term = searchTerm !== undefined ? searchTerm : searchQuery;
        const response = await getStudentResponsesByStudent(
          answerSheetId,
          studentId,
          page,
          pagination.itemsPerPage,
          status ?? undefined,
          term
        );

        setStudentResponses(response.responses);
        setPagination(response.pagination);

        if (
          response.responses.length > 0 &&
          response.responses[0].student?.name
        ) {
          setStudentName(response.responses[0].student.name);
        }
      } catch (error) {
        console.error("Erro ao buscar respostas por status:", error);
      } finally {
        setLoadingResponses(false);
      }
    },
    [
      answerSheetId,
      studentId,
      getStudentResponsesByStudent,
      pagination.itemsPerPage,
      searchQuery,
    ]
  );

  // Debounce for search
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      const trimmedValue = value.trim();
      const isNumeric = /^\d+$/.test(trimmedValue);

      if (
        (isNumeric && trimmedValue.length >= 1) ||
        (!isNumeric && trimmedValue.length >= 3)
      ) {
        setSearchQuery(trimmedValue);
        fetchResponsesByStatus(tabs[activeTab].status, 1, trimmedValue);
      } else {
        setSearchQuery("");
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
        fetchResponsesByStatus(tabs[activeTab].status, 1, "");
      }
      setIsSearching(false);
    }, 500),
    [fetchResponsesByStatus, tabs, activeTab]
  );

  // Handler for change in search input
  const handleSearchInputChange = useCallback(
    (value: string) => {
      setSearchInputValue(value);
      setIsSearching(true);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  // Fires initial search and when changing tabs
  useEffect(() => {
    fetchResponsesByStatus(tabs[activeTab].status, 1);
  }, [fetchResponsesByStatus, tabs, activeTab]);

  /**
   * @param {StudentResponse} response - The response to get the status message from.
   * @returns {string} - The status message.
   * @description Returns the string to display in the grid, according to the response status.
   */
  const getStatusMessage = (response: StudentResponseObjectives): string => {
    if (!response.status) return "Desconhecido";

    const { error, notMarked, correct, wrong } = response.status;
    if (error) return `Erro: ${error}`;
    if (notMarked) return "Não Marcada";
    if (correct) return correct;
    if (wrong) return wrong;
    return "Desconhecido";
  };

  // Items to display in the grid
  const gridItems = studentResponses.map((response) => ({
    _id: response.id,
    questionId: response.question?.id || "N/A",
    statement: response.subquestions?.[0]?.statement || "Sem enunciado",
    numberQuestion: response.numberQuestion || "N/A",
    educationLevel:
      mapEducationLevel(response.question?.educationLevel || "") ||
      "Não informado",
    difficultyLevel:
      mapDifficultyLevel(response.question?.difficultyLevel || "") ||
      "Não informado",
    studentName: response.student?.name || "Não informado",
    statusMessage: getStatusMessage(response),
    createdAt: response.createdAt,
  }));

  // Change page in pagination
  const handleChangePagination = useCallback(
    (newPage: number) => {
      fetchResponsesByStatus(tabs[activeTab].status, newPage);
    },
    [fetchResponsesByStatus, tabs, activeTab]
  );

  // Change tab
  const handleChangeTab = useCallback(
    (event: React.ChangeEvent<{}>, newValue: number): void => {
      setActiveTab(newValue);
      fetchResponsesByStatus(tabs[newValue].status, 1);
    },
    [fetchResponsesByStatus, tabs]
  );

  // Opens modal ("question details view")
  const handleManagerActionClick = useCallback(
    (responseId: string) => {
      const item = gridItems.find((item) => String(item._id) === responseId);
      if (item && item.questionId !== "N/A") {
        setModalQuestionId(item.questionId);
        setShowModalManage(true);
      } else {
        console.error(
          "questionId não encontrado para o response id:",
          responseId
        );
      }
    },
    [gridItems]
  );

  const handleCloseModalManage = useCallback(() => {
    setShowModalManage(false);
    setModalQuestionId(null);
  }, []);

  // Function that opens the modal for a specific item
  const handleOpenUpdateStatusModal = (responseId: string) => {
    const item = studentResponses.find(
      (resp) => String(resp.id) === responseId
    );
    if (item) {
      let mappedStatus: "correct" | "wrong" | "notMarked" = "correct"; 
      if (item.status?.notMarked) mappedStatus = "notMarked";
      else if (item.status?.wrong) mappedStatus = "wrong";
      else if (item.status?.correct) mappedStatus = "correct";

      setSelectedResponseId(responseId);
      setSelectedResponseStatus(mappedStatus);
      setShowModalUpdateStatus(true);
    }
  };

  // Closes the update modal
  const handleCloseUpdateStatusModal = () => {
    setShowModalUpdateStatus(false);
    setSelectedResponseId("");
    setSelectedResponseStatus("correct");
  };

  const handleUpdateStatus = async (responseId: string, newStatus: string) => {
    let updateData = { is_wrong: false };

    if (newStatus === "wrong") {
      updateData.is_wrong = true;
    }

    try {
      await updateStudentResponse({
        id: responseId,
        ...updateData,
      });

      let nextTabIndex = 0;
      if (newStatus === "wrong") {
        nextTabIndex = 2;
      } else if (newStatus === "correct") {
        nextTabIndex = 1;
      }

      setActiveTab(nextTabIndex);

      fetchResponsesByStatus(tabs[nextTabIndex].status, 1);
    } catch (error) {
      console.error("Erro ao atualizar status:", error);
    }
  };

  return (
    <div className="container-fluid py-3 px-3 bg-light">
      <header className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold">
            Respostas do Aluno: {studentName || "Não definido"}
          </h2>
          <p className="text-muted">
            Confira abaixo as respostas enviadas pelo aluno para análise
            detalhada.
          </p>
        </div>
        <DefaultButton
          bg="secondary"
          text="Voltar"
          icon={<RiArrowGoBackLine />}
          to="-1"
        />
      </header>

      <Tabs
        value={activeTab}
        onChange={handleChangeTab}
        style={{ backgroundColor: "#fafafa", marginBottom: "1rem" }}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
        scrollButtons="on"
      >
        {tabs.map((tab, index) => (
          <Tab key={index} label={tab.label} />
        ))}
      </Tabs>

      <Grid
        search_input_value={searchInputValue}
        search_text="Pesquisar por enunciado ou número da questão"
        onChangeSearchInput={handleSearchInputChange}
        onSearchButtonClick={() =>
          fetchResponsesByStatus(tabs[activeTab].status, 1)
        }
        loading_search={isSearching}
        loading_screen={loadingResponses}
        fields={[
          { name: "numberQuestion", type: "number", header: "Questão" },
          { name: "statement", type: "shortText", header: "Enunciado" },
          { name: "difficultyLevel", type: "text", header: "Dificuldade" },
          { name: "educationLevel", type: "text", header: "Nível de Ensino" },
          { name: "statusMessage", type: "statusMessage", header: "Status" },
        ]}
        items={gridItems}
        onManagerActionClick={handleManagerActionClick}
        onEditActionClick={handleOpenUpdateStatusModal}
        canEditItem={(item) => {
          // Example: if 'statusMessage' includes "Não Marcada" or "Erro:", then cannot edit.
          // If it is "Anything other than these two" => can.

          const message = item.statusMessage?.toLowerCase() || "";

          // If it contains "não marcada" or "erro:" => false
          if (message.includes("não marcada") || message.includes("erro:")) {
            return false;
          }

          // Otherwise, we assume it is correct or wrong => true
          return true;
        }}
        not_found_message="Nenhuma resposta encontrada."
        pagination={{
          last_page: pagination.totalPages,
          current_page: pagination.currentPage,
        }}
        onChangePagination={handleChangePagination}
        top_text="Respostas cadastradas"
      />

      {/* Modal to manage question details */}
      <ModalManage
        id={modalQuestionId}
        token={token}
        showModalManage={showModalManage}
        setShowModalManage={handleCloseModalManage}
      />

      {/* Modal to update response STATUS */}
      <ModalUpdateStatus
        responseId={selectedResponseId}
        currentStatus={selectedResponseStatus}
        isOpen={showModalUpdateStatus}
        onClose={handleCloseUpdateStatusModal}
        onConfirm={handleUpdateStatus}
      />
    </div>
  );
}
